/**
 * FrameSnapshot — a note's captured frame inside the hover preview box. The
 * stored image already has the reviewer's markup baked in, so it is shown as
 * is. Mounted only while its marker is hovered, so the fetch runs only then.
 */
import { useCallback, useState, type ReactNode } from 'react';

import { useThreadFile } from '@discovr/features';

import { cn } from '../../../../../lib/utils';
import { PREVIEW_BOX_CLASS, previewBoxStyle, type PreviewSize } from './previewFrame';

export const SNAPSHOT_TESTID = 'marker-snapshot';
export const SNAPSHOT_LOADING_TESTID = 'marker-snapshot-loading';
const SNAPSHOT_IMAGE_CLASS = cn(PREVIEW_BOX_CLASS, 'object-cover');
const SNAPSHOT_LOADING_CLASS = cn(PREVIEW_BOX_CLASS, 'animate-pulse');

/** Where a note's frame lives: on the server once sent, or still local while its send is optimistic. */
export interface PreviewSnapshot {
  entryId: string;
  fileId?: string;
  localUrl?: string;
}

export interface FrameSnapshotProps {
  snapshot: PreviewSnapshot;
  size: PreviewSize;
  /** Shown instead when the frame can't be loaded — the video's own frame at that time. */
  fallback: ReactNode;
}

export function FrameSnapshot({ snapshot, size, fallback }: FrameSnapshotProps) {
  const { entryId, fileId, localUrl } = snapshot;
  // An optimistic note already holds its frame in memory; only a sent one is fetched.
  const file = useThreadFile(entryId, fileId, { enabled: !localUrl });
  const [imageBroken, setImageBroken] = useState(false);
  const markImageBroken = useCallback(() => setImageBroken(true), []);
  const url = localUrl ?? file.url;
  const style = previewBoxStyle(size);

  if (imageBroken || file.isError) return <>{fallback}</>;

  if (!url) {
    return <span aria-hidden="true" data-testid={SNAPSHOT_LOADING_TESTID} className={SNAPSHOT_LOADING_CLASS} style={style} />;
  }

  return <img src={url} alt="" data-testid={SNAPSHOT_TESTID} className={SNAPSHOT_IMAGE_CLASS} style={style} onError={markImageBroken} />;
}
